// ROOM 404: Society Events, RSVPs, Waitlist & Check-in Verification

import React, { useEffect, useState } from 'react';
import {
  Calendar,
  CheckCircle,
  Clock,
  MapPin,
  QrCode,
  Sparkles,
  Users,
  X,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../services/api';
import type { EventItem } from '../types';

export const Events: React.FC = () => {
  const { isStaff, refreshSession } = useAuth();
  const [events, setEvents] = useState<EventItem[]>([]);
  const [selectedEventForCheckin, setSelectedEventForCheckin] = useState<EventItem | null>(null);
  const [scannedQrToken, setScannedQrToken] = useState('');
  const [checkinResult, setCheckinResult] = useState<any>(null);
  const [checkinError, setCheckinError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchEvents = async () => {
    try {
      const data = await apiRequest<{ events: EventItem[] }>('/api/events');
      setEvents(data.events);
    } catch {
      // Ignored
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleRsvp = async (eventId: string) => {
    try {
      const res = await apiRequest<{ status: string; message: string }>(`/api/events/${eventId}/rsvp`, {
        method: 'POST',
      });
      alert(res.message);
      fetchEvents();
    } catch (err: any) {
      alert(err.message || 'RSVP failed');
    }
  };

  const handleCancelRsvp = async (eventId: string) => {
    if (!confirm('Cancel your RSVP? If there is a waitlist, your spot will be given to the next student.'))
      return;
    try {
      await apiRequest(`/api/events/${eventId}/cancel-rsvp`, { method: 'POST' });
      fetchEvents();
    } catch (err: any) {
      alert(err.message || 'Cancel failed');
    }
  };

  const handleValidateCheckin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEventForCheckin || !scannedQrToken.trim()) return;

    setCheckinError(null);
    setCheckinResult(null);

    try {
      const res = await apiRequest(`/api/events/${selectedEventForCheckin.id}/checkin`, {
        method: 'POST',
        body: { qrToken: scannedQrToken.trim() },
      });

      setCheckinResult(res);
      setScannedQrToken('');
      fetchEvents();
      refreshSession();
    } catch (err: any) {
      setCheckinError(err.message || 'Check-in validation failed');
    }
  };

  return (
    <div className="space-y-3 pb-24 max-w-md mx-auto px-4 pt-3">
      {/* Header */}
      <div>
        <h2 className="text-lg font-bold text-white tracking-tight">Society Events</h2>
        <p className="text-xs text-gray-400">Verified student gatherings, meetups & check-ins</p>
      </div>

      {/* Events List */}
      <div className="space-y-3">
        {events.map((ev) => {
          const isConfirmed = ev.user_rsvp_status === 'confirmed';
          const isWaitlisted = ev.user_rsvp_status === 'waitlisted';
          const capacityPercent = Math.min(100, Math.round(((ev.confirmed_count || 0) / ev.capacity) * 100));

          return (
            <div
              key={ev.id}
              className="p-4 rounded-2xl bg-[#12141c] border border-[#1f2433] space-y-3"
            >
              <div className="flex items-center justify-between text-xs text-cyan-400 font-mono">
                <span className="flex items-center space-x-1.5">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>
                    {new Date(ev.event_time).toLocaleDateString([], {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </span>

                <span className="text-amber-400 font-bold flex items-center space-x-1">
                  <Sparkles className="w-3 h-3" />
                  <span>+25 pts</span>
                </span>
              </div>

              <div>
                <h3 className="text-sm font-bold text-white tracking-tight">{ev.title}</h3>
                <p className="text-xs text-gray-300 mt-1 leading-relaxed">{ev.description}</p>
              </div>

              <div className="flex items-center space-x-1.5 text-xs text-gray-400">
                <MapPin className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                <span>{ev.approximate_location}</span>
              </div>

              {/* Capacity Progress Bar */}
              <div>
                <div className="flex items-center justify-between text-[11px] text-gray-400 font-mono mb-1">
                  <span>
                    Capacity ({ev.confirmed_count || 0}/{ev.capacity})
                  </span>
                  <span>{capacityPercent}% filled</span>
                </div>
                <div className="w-full bg-[#090a0f] h-1.5 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all ${
                      capacityPercent >= 100 ? 'bg-amber-400' : 'bg-emerald-400'
                    }`}
                    style={{ width: `${capacityPercent}%` }}
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-2 border-t border-[#1f2433]/70">
                {isConfirmed ? (
                  <div className="flex items-center space-x-2">
                    <span className="px-3 py-1 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-xs font-semibold flex items-center space-x-1">
                      <CheckCircle className="w-3.5 h-3.5" />
                      <span>RSVP Confirmed</span>
                    </span>
                    <button
                      onClick={() => handleCancelRsvp(ev.id)}
                      className="text-xs text-gray-500 hover:text-red-400 underline"
                    >
                      Cancel
                    </button>
                  </div>
                ) : isWaitlisted ? (
                  <div className="flex items-center space-x-2">
                    <span className="px-3 py-1 rounded-xl bg-amber-500/20 text-amber-400 border border-amber-500/30 text-xs font-semibold flex items-center space-x-1">
                      <Clock className="w-3.5 h-3.5" />
                      <span>On Waitlist</span>
                    </span>
                    <button
                      onClick={() => handleCancelRsvp(ev.id)}
                      className="text-xs text-gray-500 hover:text-red-400 underline"
                    >
                      Leave Waitlist
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => handleRsvp(ev.id)}
                    className="px-4 py-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-semibold transition-colors"
                  >
                    RSVP Spot
                  </button>
                )}

                {/* Organizer Rotating QR check-in tool */}
                {isStaff && (
                  <button
                    onClick={() => setSelectedEventForCheckin(ev)}
                    className="p-1.5 rounded-xl bg-[#1f2433] hover:bg-cyan-500/20 text-cyan-400 text-xs font-medium flex items-center space-x-1 transition-colors"
                    title="Validate Member Check-in"
                  >
                    <QrCode className="w-3.5 h-3.5" />
                    <span>Scan Check-in</span>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Organizer Checkin Verification Modal */}
      {selectedEventForCheckin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm">
          <div className="w-full max-w-sm bg-[#12141c] border border-[#1f2433] rounded-2xl p-5 shadow-2xl">
            <div className="flex items-center justify-between pb-2 border-b border-[#1f2433] mb-3">
              <div>
                <h3 className="text-sm font-bold text-white">Event Check-in Validator</h3>
                <p className="text-[11px] text-gray-400 truncate">{selectedEventForCheckin.title}</p>
              </div>
              <button
                onClick={() => {
                  setSelectedEventForCheckin(null);
                  setCheckinResult(null);
                  setCheckinError(null);
                }}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {checkinResult && (
              <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs space-y-1 mb-3">
                <div className="font-bold flex items-center space-x-1">
                  <CheckCircle className="w-4 h-4" />
                  <span>Check-in Verified!</span>
                </div>
                <div>{checkinResult.message}</div>
              </div>
            )}

            {checkinError && (
              <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-xs mb-3">
                {checkinError}
              </div>
            )}

            <form onSubmit={handleValidateCheckin} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">
                  Paste or Scan Student's Rotating QR Token
                </label>
                <textarea
                  required
                  rows={3}
                  value={scannedQrToken}
                  onChange={(e) => setScannedQrToken(e.target.value)}
                  placeholder="Paste QR token payload here..."
                  className="w-full bg-[#090a0f] border border-[#1f2433] rounded-xl px-3 py-2 text-[11px] font-mono text-gray-200 focus:outline-none focus:border-emerald-500 resize-none"
                />
              </div>

              <button
                type="submit"
                className="w-full py-2 px-3 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold rounded-xl text-xs transition-colors"
              >
                Verify Token & Record Check-in
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
